import React, { useEffect, useState } from 'react';
import { X, Mail, Eye, EyeOff, Loader2, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup';
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const { signIn, signUp, resetPassword } = useAuth();

  useEffect(() => {
    if (!isOpen) return;

    setMode(initialMode);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setError('');
    setMessage('');
    setShowPassword(false);
  }, [initialMode, isOpen]);

  // Monitor network status
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isOnline) {
      setError('No internet connection. Please check your network and try again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (mode === 'signup') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          return;
        }

        const { error } = await signUp(email, password, {
          full_name: fullName,
        });

        if (error) {
          if (error.message.includes('fetch')) {
            setError('Network error: Unable to connect to authentication service. Please check your internet connection and try again.');
          } else {
            setError(error.message);
          }
        } else {
          setMessage('Check your email for the confirmation link!');
        }
      } else if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('fetch')) {
            setError('Network error: Unable to connect to authentication service. Please check your internet connection and try again.');
          } else {
            setError(error.message);
          }
        } else {
          onClose();
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          if (error.message.includes('fetch')) {
            setError('Network error: Unable to connect to authentication service. Please check your internet connection and try again.');
          } else {
            setError(error.message);
          }
        } else {
          setMessage('Check your email for the password reset link!');
        }
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Network error: Unable to connect to authentication service. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setError('');
    setMessage('');
    setShowPassword(false);
  };

  const switchMode = (newMode: 'signin' | 'signup' | 'forgot') => {
    setMode(newMode);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-[28px] border border-border bg-surface-raised/95 shadow-modal backdrop-blur-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-content">
                {mode === 'signin' && 'Welcome Back'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'forgot' && 'Reset Password'}
              </h2>
              <p className="mt-1 text-sm text-content-secondary">
                {mode === 'signin' && 'Sign in to your ElectroPLM account'}
                {mode === 'signup' && 'Join the ElectroPLM community'}
                {mode === 'forgot' && 'Enter your email to reset your password'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-content-muted transition-colors hover:bg-surface-overlay hover:text-content"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Network Status */}
          {!isOnline && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <WifiOff className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-600 font-medium">No Internet Connection</p>
                <p className="text-xs text-red-500 mt-1">Please check your network connection and try again.</p>
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-600">{error}</p>
                {error.includes('Network error') && (
                  <div className="mt-2 text-xs text-red-500">
                    <p>Troubleshooting tips:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Check your internet connection</li>
                      <li>Try refreshing the page</li>
                      <li>Disable any VPN or proxy</li>
                      <li>Check if your firewall is blocking the connection</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{message}</p>
            </div>
          )}

          {/* Connection Status */}
            <div className="mb-4 flex items-center justify-between rounded-2xl border border-border bg-surface-overlay/70 p-2.5">
            <div className="flex items-center">
              {isOnline ? (
                <Wifi className="h-4 w-4 text-green-500 mr-2" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-500 mr-2" />
              )}
              <span className="text-xs text-content-secondary">
                {isOnline ? 'Connected' : 'Offline'}
              </span>
            </div>
            <span className="text-xs text-content-muted">
              {window.location.hostname}
            </span>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label htmlFor="fullName" className="mb-1 block text-sm font-medium text-content-secondary">
                  Full Name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="input"
                  placeholder="Enter your full name"
                  required
                  disabled={!isOnline}
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-content-secondary">
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="Enter your email"
                  required
                  disabled={!isOnline}
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-content-muted" />
              </div>
            </div>

            {mode !== 'forgot' && (
              <div>
                <label htmlFor="password" className="mb-1 block text-sm font-medium text-content-secondary">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="Enter your password"
                    required
                    minLength={6}
                    disabled={!isOnline}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-muted hover:text-content"
                    disabled={!isOnline}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-content-secondary">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="Confirm your password"
                  required
                  minLength={6}
                  disabled={!isOnline}
                />
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
              disabled={loading || !isOnline}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {mode === 'signin' && 'Signing In...'}
                  {mode === 'signup' && 'Creating Account...'}
                  {mode === 'forgot' && 'Sending Reset Link...'}
                </div>
              ) : (
                <>
                  {mode === 'signin' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'forgot' && 'Send Reset Link'}
                </>
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            {mode === 'signin' && (
              <>
                <button
                  onClick={() => switchMode('forgot')}
                  className="text-sm font-medium text-primary-600 hover:text-primary-800"
                  disabled={!isOnline}
                >
                  Forgot your password?
                </button>
                <div className="text-sm text-content-secondary">
                  Don't have an account?{' '}
                  <button
                    onClick={() => switchMode('signup')}
                    className="text-primary-600 hover:text-primary-800 font-medium"
                    disabled={!isOnline}
                  >
                    Sign up
                  </button>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <div className="text-sm text-content-secondary">
                Already have an account?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-primary-600 hover:text-primary-800 font-medium"
                  disabled={!isOnline}
                >
                  Sign in
                </button>
              </div>
            )}

            {mode === 'forgot' && (
              <div className="text-sm text-content-secondary">
                Remember your password?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-primary-600 hover:text-primary-800 font-medium"
                  disabled={!isOnline}
                >
                  Sign in
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;